/**
 * "Legg til i kalenderen" — the add-to-meal-plan flow shared by the recipe
 * detail page's desktop dialog and a recipe card's mobile "+" quick action
 * (a bottom sheet). ONE component, TWO presentations: the markup is
 * identical, `styles/app.css` alone decides whether it renders as a
 * centered modal (desktop) or a bottom sheet (mobile) via a media query —
 * see `.add-calendar-dialog`.
 *
 * Week/day pickers are plain chip buttons (no native <select>, matching the
 * design handoff); servings defaults to the recipe's base `servings` (or 1)
 * and is adjustable with a −/+ stepper, same idiom as the calendar entry's
 * own stepper in `MealPlannerPanel`.
 */

import type { CreatePlannedMealInput } from "@tjommi/shared";
import React, { useMemo, useState } from "react";

import { UnauthorizedError, addPlannedMeal } from "../api";
import { dateForDayInWeek, getWeek } from "../lib/week";

const MIN_SERVINGS = 1;
const MAX_SERVINGS = 100;

const WEEK_CHOICES: { offset: number; label: string }[] = [
  { offset: 0, label: "Denne uken" },
  { offset: 1, label: "Neste uke" },
  { offset: 2, label: "Om 2 uker" },
];

export function AddToCalendarDialog({
  cookbookId,
  recipeId,
  recipeTitle,
  baseServings,
  accessToken,
  onClose,
  onSessionExpired,
  onAdded,
}: {
  cookbookId: string;
  recipeId: string;
  recipeTitle: string;
  /** The recipe's own base `servings`, used as the picker's default — or null. */
  baseServings: number | null;
  accessToken: string;
  onClose: () => void;
  onSessionExpired: () => void;
  /** Called after a successful add — e.g. so the caller can show a confirmation. */
  onAdded?: () => void;
}): React.JSX.Element {
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayIndex, setDayIndex] = useState(0);
  const [servings, setServings] = useState(Math.max(MIN_SERVINGS, baseServings ?? 1));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const week = useMemo(() => getWeek(weekOffset), [weekOffset]);

  function stop(e: React.MouseEvent): void {
    e.stopPropagation();
  }

  async function handleConfirm(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const input: CreatePlannedMealInput = {
      recipeId,
      plannedDate: dateForDayInWeek(weekOffset, dayIndex),
      servings,
    };

    try {
      await addPlannedMeal(cookbookId, input, accessToken);
      setSubmitting(false);
      onAdded?.();
      onClose();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setError("Kunne ikke legge til i kalenderen. Prøv igjen.");
    }
  }

  return (
    <div className="dlg-overlay" data-testid="add-to-calendar-overlay" onClick={onClose}>
      <div
        className="dlg-panel add-calendar-dialog"
        data-testid="add-to-calendar-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Legg til i kalenderen"
        onClick={stop}
      >
        <div className="dlg-sheet-handle" aria-hidden="true" />
        <div className="dlg-title">Legg til i kalenderen</div>
        <div className="dlg-subtitle" data-testid="add-to-calendar-recipe-title">
          {recipeTitle}
        </div>

        <div className="dlg-label">Uke</div>
        <div className="dlg-chip-row" role="radiogroup" aria-label="Uke">
          {WEEK_CHOICES.map((choice) => (
            <button
              key={choice.offset}
              type="button"
              className={`dlg-chip${weekOffset === choice.offset ? " is-active" : ""}`}
              aria-pressed={weekOffset === choice.offset}
              data-testid={`add-week-${choice.offset}`}
              onClick={() => setWeekOffset(choice.offset)}
            >
              {choice.label}
            </button>
          ))}
        </div>

        <div className="dlg-label">Dag</div>
        <div className="dlg-chip-row dlg-day-row" role="radiogroup" aria-label="Dag">
          {week.days.map((day) => (
            <button
              key={day.date}
              type="button"
              className={`dlg-chip${dayIndex === day.dayIndex ? " is-active" : ""}`}
              aria-pressed={dayIndex === day.dayIndex}
              data-testid={`add-day-${day.dayIndex}`}
              onClick={() => setDayIndex(day.dayIndex)}
            >
              {day.label}
            </button>
          ))}
        </div>

        <div className="dlg-label">Porsjoner</div>
        <div className="dlg-servings">
          <button
            type="button"
            className="planner-stepper-btn"
            data-testid="add-servings-dec"
            aria-label="Færre porsjoner"
            onClick={() => setServings((s) => Math.max(MIN_SERVINGS, s - 1))}
          >
            −
          </button>
          <span data-testid="add-servings-value">{servings} porsjoner</span>
          <button
            type="button"
            className="planner-stepper-btn"
            data-testid="add-servings-inc"
            aria-label="Flere porsjoner"
            onClick={() => setServings((s) => Math.min(MAX_SERVINGS, s + 1))}
          >
            +
          </button>
        </div>

        {error && (
          <p className="dlg-error" data-testid="add-to-calendar-error" role="alert">
            {error}
          </p>
        )}

        <div className="dlg-actions">
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="add-to-calendar-cancel"
            onClick={onClose}
          >
            Avbryt
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="add-to-calendar-confirm"
            disabled={submitting}
            onClick={() => void handleConfirm()}
          >
            {submitting ? "Legger til …" : "Legg til"}
          </button>
        </div>
      </div>
    </div>
  );
}
